import { resources } from "@constants/hardcoded-strings";
import { NULL_ENCODED_VALUE } from "@constants/verification-transaction-values";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { SelectOption } from "@idw-types/common";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { FormattedIdentityProviderConfiguration } from "@idw-types/data/identity-providers";
import {
    AxnCountry,
    AxnRelyingParty,
    AxnRPServiceWithSOID,
    AxnSelectedServiceOfferingSO,
    SimpleAxnWorkflow,
} from "@idw-types/data/reference-data/axn";
import {
    Alert,
    Assertion,
    AttributeType,
    ClaimStatus,
    Company,
    PolicyDecision,
    RelyingParty,
    RiskLevel,
    RpServiceTemplate,
    ServiceOffering,
    SelfServiceVerificationMessageTemplate,
    WorkflowTemplate
} from "@idw-types/data/reference-data/generic";
import { ServiceAssertionPerformanceRevision, ServiceAssertionPerformanceService } from "@idw-types/data/reports";
import { RpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { FormattedVerificationServiceConfiguration } from "@idw-types/data/verification-service-configuration";
import { FormattedWorkflowConfiguration, WorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import format from "date-fns/format";
import { sortStrings } from "./string-util";

export const mapSelectOptionsToValues = (selectOptions: Array<SelectOption>): Array<string> => {
    return selectOptions.map(selectOption => selectOption.value);
};

//name and id
export const mapEntitiesWithNameAndIdToSelectOptions = (
    entities: Array<
        Company |
        Alert |
        WorkflowConfiguration |
        RelyingParty |
        AxnRelyingParty |
        ServiceOffering |
        AxnSelectedServiceOfferingSO |
        SimpleAxnWorkflow |
        AttributeType |
        AxnCountry |
        SelfServiceVerificationMessageTemplate |
        FormattedIdentityProviderConfiguration |
        RpServiceTemplate |
        AxnAdminUserPropertyBundle |
        FormattedMessageTemplate |
        WorkflowTemplate
    >
): Array<SelectOption> => {
    return entities.map(entity => mapEntityWithNameAndIdToSelectOption(entity)).filter(x => x);
};

export const mapEntityWithNameAndIdToSelectOption = (
    entity: Company |
        Alert |
        WorkflowConfiguration |
        RelyingParty |
        AxnRelyingParty |
        ServiceOffering |
        AxnSelectedServiceOfferingSO |
        SimpleAxnWorkflow |
        AttributeType |
        AxnCountry |
        SelfServiceVerificationMessageTemplate |
        FormattedIdentityProviderConfiguration |
        RpServiceTemplate |
        AxnAdminUserPropertyBundle |
        FormattedMessageTemplate |
        WorkflowTemplate
): SelectOption => {
    return entity ? { label: entity.name, value: entity.id } : null;
};


// value and key
export const mapEntitiesWithValueAndKeyToSelectOptions = (
    entities: Array<PolicyDecision | ClaimStatus>
): Array<SelectOption> => {
    return entities.map(entity => mapEntityWithValueAndKeyToSelectOption(entity));
};

export const mapEntityWithValueAndKeyToSelectOption = (
    entity: PolicyDecision | ClaimStatus
): SelectOption => {
    return { label: entity.value, value: entity.key };
};

export const mapCountriesToSelectOptions = (
    entities: Array<AxnCountry>
): Array<SelectOption> => {
    return entities.map(entity => mapCountryToSelectOption(entity)).sort((a, b) => sortStrings(a.label, b.label));
};

export const mapCountryToSelectOption = (
    entity: AxnCountry
): SelectOption => {
    if (entity.alpha2 === NULL_ENCODED_VALUE) {
        return { label: entity.name, value: entity.alpha2 };
    } else {
        return { label: `${entity.alpha2} - ${entity.name}`, value: entity.alpha2 };
    }
};


//rp service configuration
export const mapRpServicesToSelectOptions = (
    rpServices: Array<RpServiceConfiguration | ServiceAssertionPerformanceService | AxnRPServiceWithSOID | FormattedVerificationServiceConfiguration | AxnRpService>
): Array<SelectOption> => {
    return rpServices.map(rpService => mapRpServiceToSelectOption(rpService));
};

export const mapRpServiceToSelectOption = (
    rpService: RpServiceConfiguration | ServiceAssertionPerformanceService | AxnRPServiceWithSOID | FormattedVerificationServiceConfiguration | AxnRpService
): SelectOption => {
    return {
        label: `${rpService.serviceName} (${rpService.apiKey})`,
        value: rpService.id
    };
};

//workflow configuration
export const mapWorkflowsToSelectOptions = (
    workflowConfigurations: Array<FormattedWorkflowConfiguration>
): Array<SelectOption> => {
    return workflowConfigurations.map(wc => ({ label: `${wc.name}`, value: `${wc.id}`}));
};

// assertions
export const mapAssertionsToSelectOptions = (assertions: Array<Assertion>): Array<SelectOption> => {
    return assertions.map(assertion => mapAssertionToSelectOption(assertion));
};

export const mapAssertionToSelectOption = (assertion: Assertion): SelectOption => {
    return { label: assertion.shortName, value: assertion.id };
};

// risk levels
export const mapRiskLevelsToSelectOptions = (riskLevels: Array<RiskLevel>): Array<SelectOption> => {
    return riskLevels.map(riskLevel => mapRiskLevelToSelectOption(riskLevel));
};

export const mapRiskLevelToSelectOption = (riskLevel: RiskLevel): SelectOption => {
    return { label: riskLevel.label, value: riskLevel.id };
};

// service assertion performance service
export const mapSAPServiceRevisionsToSelectOptions = (sapServiceRevisions: Array<ServiceAssertionPerformanceRevision>): Array<SelectOption> => {
    return sapServiceRevisions.map(sapServiceRevision => mapSAPServiceRevisionToSelectOption(sapServiceRevision));
};

export const mapSAPServiceRevisionToSelectOption = (sapServiceRevision: ServiceAssertionPerformanceRevision): SelectOption => {
    const startDate = sapServiceRevision.startDate ? new Date(sapServiceRevision.startDate) : null;
    const endDate = sapServiceRevision.endDate ? new Date(sapServiceRevision.endDate) : null;
    let label = `${sapServiceRevision.revision}: `;
    if (startDate) {
        label += `${format(startDate, "MMMM do, yyyy")} - `;
    }
    if (endDate) {
        label += `${format(endDate, "MMMM do, yyyy")}`;
    } else {
        label += `${resources.pages.serviceAssertionPerformance.searchForm.filters.verificationServiceRevision.presentText}`;
    }

    return {
        label: label,
        value: sapServiceRevision.revision
    };
};

export const mapWorkflowsToSelectOptionsWithStyle = (workflows: Array<SimpleAxnWorkflow>) => {
    return workflows.map(workflow => mapWorkflowToSelectOptionWithStyle(workflow));
};

export const mapWorkflowToSelectOptionWithStyle = (workflow: SimpleAxnWorkflow) => {
    return {
        label: workflow.name,
        value: workflow.id,
        color: workflow.status === EntityStatusTypes.INACTIVE ? "#a09f9f" : undefined,
        fontStyle: workflow.status === EntityStatusTypes.INACTIVE ? "italic" : undefined
    };
};

export const mapRpServicesWithSOIDToSelectOptionsWithStyle = (rpServices: Array<AxnRPServiceWithSOID>) => {
    return rpServices.map(service => mapRpServiceWithSOIDToSelectOptionWithStyle(service));
};

export const mapRpServiceWithSOIDToSelectOptionWithStyle = (
    rpService: AxnRPServiceWithSOID
): SelectOption => {
    return {
        label: `${rpService.serviceName} (${rpService.apiKey})`,
        value: rpService.id,
        color: rpService.status === EntityStatusTypes.INACTIVE ? "#a09f9f" : undefined,
        fontStyle: rpService.status === EntityStatusTypes.INACTIVE ? "italic" : undefined
    };
};